import React, { useState, useEffect, useCallback, useRef } from 'react';
import { HashRouter, Routes, Route, useNavigate, Navigate, useSearchParams, Outlet, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProductCard from './components/ProductCard';
import ProductListRow from './components/ProductListRow';
import ProductDetailPage from './components/ProductDetailPage';
import CartDrawer from './components/CartDrawer';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import UserLogin from './components/UserLogin';
import MyPage from './components/MyPage';
import ChannelTalk from './components/ChannelTalk';
import LandingPage from './components/LandingPage';
import BrandStoryPage from './components/BrandStoryPage';
import CompetitionsPage from './components/CompetitionsPage'; 
import LibraryPage from './components/LibraryPage'; 
import LoadingScreen from './components/LoadingScreen'; 
// 에러 원인 제거: import GlobalDialog from './components/GlobalDialog'; (삭제됨)
import { MOCK_PRODUCTS } from './constants';
import { Product, CartItem, User, Order, OrderStatus } from './types';
import { requestPayment, cancelPayment } from './services/paymentService'; 
import { auth, db, onAuthStateChanged, signOut, doc, getDoc, getDocs, collection, deleteDoc, setDoc, onSnapshot, query, orderBy, updateDoc, addDoc, where } from './firebase';

const HomePage: React.FC<{ products: Product[]; categories: string[]; addToCart: (item: Omit<CartItem, 'cartId'>, startRect?: DOMRect) => void; }> = ({ products, categories, addToCart }) => {
  const [selectedCategories, setSelectedCategories] = useState<string[]>(["ALL"]);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleCount, setVisibleCount] = useState(50);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeFilter = searchParams.get('filter');

  const [currentSlide, setCurrentSlide] = useState(0);
  
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const bannerSlides = [
    {
      badge: "WELCOME EVENT",
      title: "상상하는 즐거움,\n지금 바로 시작하세요",
      desc: "가입 즉시 3,000 포인트를 드립니다.",
      btnText: "선물 받고 시작하기",
      overlay: "from-indigo-950/90 via-indigo-900/60 to-transparent",
      image: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/banner1.jpg?alt=media&token=cebea0c0-3974-45c4-bd13-5176eaecd25c", 
      target: "/signup"
    },
    {
      badge: "NEW ARRIVALS",
      title: "창의력의 경계를 넓히는\n이달의 신상품",
      desc: "최신 테크놀로지 키트를 만나보세요.",
      btnText: "신상품 보러가기",
      overlay: "from-blue-950/90 via-blue-900/60 to-transparent",
      image: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/banner2.jpg?alt=media&token=6031c717-1848-4d85-953e-da8c1cb20818",
      target: "/shop?filter=new"
    },
    {
      badge: "HOT DEAL",
      title: "메이커스토리 한정 특가\n최대 20% 할인 혜택",
      desc: "놓치면 후회할 베스트 아이템 기획전",
      btnText: "할인 상품 바로가기",
      overlay: "from-rose-950/90 via-rose-900/60 to-transparent",
      image: "https://firebasestorage.googleapis.com/v0/b/makerstory-shopping.firebasestorage.app/o/banner3.jpg?alt=media&token=0aa1c9d2-0070-47b7-bc67-ac9a137be841",
      target: "/shop?filter=sale"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [bannerSlides.length]);

  const handleSwipe = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) setCurrentSlide(prev => (prev + 1) % bannerSlides.length);
    else if (distance < -50) setCurrentSlide(prev => (prev - 1 + bannerSlides.length) % bannerSlides.length);
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const filteredProducts = products.filter(p => {
    const matchesCategory = selectedCategories.includes("ALL") || p.categories?.some(cat => selectedCategories.includes(cat));
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    
    let matchesFilter = true;
    if (activeFilter === 'new') {
      matchesFilter = !!p.isNew;
    } else if (activeFilter === 'sale') {
      matchesFilter = !!(p.originalPrice && p.originalPrice > p.price);
    }

    return matchesCategory && matchesSearch && matchesFilter;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const aHasDiscount = a.originalPrice && a.originalPrice > a.price;
    const bHasDiscount = b.originalPrice && b.originalPrice > b.price;
    if (aHasDiscount && !bHasDiscount) return -1;
    if (!aHasDiscount && bHasDiscount) return 1;
    return 0;
  });

  if (products.length === 0) {
    return (
      <main className="container mx-auto px-4 py-20 min-h-[60vh] flex flex-col items-center justify-center">
        <div className="relative mb-8">
           <div className="w-20 h-20 bg-indigo-600 rounded-3xl flex items-center justify-center text-white text-4xl shadow-2xl animate-spin-slow">
             ⚙️
           </div>
           <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-16 h-2 bg-black/10 rounded-full blur-md animate-pulse"></div>
        </div>
        <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 italic uppercase tracking-tighter animate-pulse text-center">
          Opening Workshop...
        </h2>
        <p className="text-gray-500 text-sm md:text-base font-medium text-center animate-in slide-in-from-bottom-2 fade-in duration-700">
          "창의력을 조립할 부품들을<br/>창고에서 열심히 나르고 있어요! 📦💨"
        </p>
        <style>{`
          @keyframes spin-slow {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .animate-spin-slow {
            animation: spin-slow 3s linear infinite;
          }
        `}</style>
      </main>
    );
  }

  return (
    <main className="container mx-auto px-4 py-8">
      <div 
        className="mb-10 relative h-[300px] md:h-[450px] rounded-[32px] md:rounded-[48px] overflow-hidden shadow-2xl touch-pan-y"
        onTouchStart={e => touchStartX.current = e.touches[0].clientX}
        onTouchMove={e => touchEndX.current = e.touches[0].clientX}
        onTouchEnd={handleSwipe}
      >
        {bannerSlides.map((slide, index) => (
          <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
            <img src={slide.image} className="absolute inset-0 w-full h-full object-cover" alt="" />
            <div className={`absolute inset-0 bg-gradient-to-r ${slide.overlay}`}></div>
            <div className="relative h-full flex items-center px-8 md:px-20">
              <div className="max-w-2xl">
                <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md text-white border border-white/20 rounded-full text-[10px] font-black mb-4 uppercase tracking-widest">{slide.badge}</span>
                <h1 className="text-3xl md:text-6xl font-black text-white italic mb-4 md:mb-8 leading-tight tracking-tighter whitespace-pre-line">{slide.title}</h1>
                <button onClick={() => navigate(slide.target)} className="px-6 md:px-10 py-3 md:py-4 bg-white text-gray-900 rounded-2xl font-black hover:scale-105 transition shadow-2xl">
                  {slide.btnText}
                </button>
              </div>
            </div>
          </div>
        ))}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2.5 px-4 py-2 bg-black/10 backdrop-blur-md rounded-full border border-white/10">
          {bannerSlides.map((_, idx) => (
            <button key={idx} onClick={() => setCurrentSlide(idx)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-white w-6' : 'bg-white/40'}`} />
          ))}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row justify-between gap-6 mb-10 items-start lg:items-center">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide flex-1 w-full lg:w-auto">
          {categories.map(c => (
            <button key={c} onClick={() => setSelectedCategories([c])} className={`px-5 py-2.5 rounded-xl text-xs font-black whitespace-nowrap transition-all ${selectedCategories.includes(c) ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white border border-gray-100 text-gray-500 hover:bg-gray-50'}`}>{c}</button>
          ))}
        </div>
        
        <div className="flex gap-3 w-full lg:w-auto items-center">
          <input type="text" placeholder="검색어를 입력하세요" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="flex-1 min-w-0 lg:w-64 px-5 py-3 rounded-xl border border-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm" />
          
          <div className="flex bg-gray-100 p-1 rounded-xl shrink-0">
            <button 
              onClick={() => setViewMode('card')} 
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'card' ? 'bg-white shadow text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="카드형 보기"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H-2a2 2 0 01-2-2V6z" /></svg>
            </button>
            <button 
              onClick={() => setViewMode('list')} 
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-gray-400 hover:text-gray-600'}`}
              title="리스트형 보기"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
        </div>
      </div>

      {activeFilter && (
        <div className="mb-6 animate-in fade-in slide-in-from-left-4 duration-500">
          <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gray-900 text-white rounded-2xl shadow-xl">
            <span className="text-xs font-black uppercase tracking-widest">
              {activeFilter === 'new' ? '✨ NEW ARRIVALS' : '⚡ HOT DEALS'} 모아보기
            </span>
            <button 
              onClick={() => navigate('/shop')}
              className="p-1 bg-white/20 rounded-full hover:bg-red-500 hover:text-white transition-colors"
              title="필터 해제"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
      )}

      <div className={viewMode === 'card' ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" : "flex flex-col gap-4 max-w-5xl mx-auto"}>
        {sortedProducts.slice(0, visibleCount).map(p => (
          viewMode === 'card' ? 
          <ProductCard key={p.id} product={p} onAddToCart={(product, rect) => addToCart({...product, quantity: 1, selectedOptions: {}, selectedAddOns: []}, rect)} /> :
          <ProductListRow key={p.id} product={p} onAddToCart={(product, rect) => addToCart({...product, quantity: 1, selectedOptions: {}, selectedAddOns: []}, rect)} />
        ))}
      </div>

      {visibleCount < sortedProducts.length && (
        <div className="mt-12 text-center">
          <button 
            onClick={() => setVisibleCount(prev => prev + 50)}
            className="px-12 py-4 bg-white border-2 border-indigo-50 text-indigo-600 rounded-2xl font-black shadow-lg hover:bg-indigo-600 hover:text-white hover:border-indigo-600 transition-all active:scale-95 flex items-center gap-2 mx-auto"
          >
            <span>상품 더 보기</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
      )}
    </main>
  );
};

const ShoppingLayout: React.FC<{
  isAuthLoading: boolean;
  isPaymentReturn: boolean;
  cartItems: CartItem[];
  setIsCartOpen: (open: boolean) => void;
  isAdmin: boolean;
  currentUser: User | null;
  handleUserLogout: () => void;
  setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  isCartOpen: boolean;
  handleCheckout: (method: string, pointsUsed?: number) => void;
  products: Product[];
}> = ({ isAuthLoading, isPaymentReturn, cartItems, setIsCartOpen, isAdmin, currentUser, handleUserLogout, setCartItems, isCartOpen, handleCheckout, products }) => {
  const navigate = useNavigate();
  
  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <Navbar cartCount={cartItems.length} onCartClick={() => setIsCartOpen(true)} onLogoClick={() => navigate('/')} isAdmin={isAdmin} currentUser={currentUser} onLogout={handleUserLogout} />
      <Outlet />
      <Footer />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} items={cartItems} onUpdateQuantity={(id, d) => setCartItems(cartItems.map(i => i.cartId === id ? { ...i, quantity: Math.max(1, i.quantity + d) } : i))} onRemoveItem={id => setCartItems(cartItems.filter(i => i.cartId !== id))} onCheckout={handleCheckout} currentUser={currentUser} />
      <ChannelTalk products={products} currentUser={currentUser} />
    </>
  );
};

const MainApp: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [products, setProducts] = useState<Product[]>(() => {
    const cached = localStorage.getItem('makerstory_products_cache');
    return cached ? JSON.parse(cached) : [];
  });

  const [users, setUsers] = useState<User[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  
  const [cancelledOrders, setCancelledOrders] = useState<Order[]>([]);
  const [categories, setCategories] = useState<string[]>(['ALL', '커넥트', '테크닉', '익사이팅', '네모', '말랑말랑코딩', 'AI비누']);
  
  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem('makerstory_user_cache');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  
  const [isAuthLoading, setIsAuthLoading] = useState(() => {
    return !localStorage.getItem('makerstory_user_cache');
  }); 

  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('makerstory_admin') === 'true');
  const [cartItems, setCartItems] = useState<CartItem[]>(() => JSON.parse(localStorage.getItem('makerstory_cart_items') || '[]'));
  const [isCartOpen, setIsCartOpen] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
  const isPaymentReturn = !!(
    urlParams.get('imp_uid') || hashParams.get('imp_uid') || 
    urlParams.get('merchant_uid') || hashParams.get('merchant_uid') ||
    urlParams.get('error_msg') || hashParams.get('error_msg') ||
    urlParams.get('orderNumber') || hashParams.get('orderNumber')
  );

  useEffect(() => {
    const checkPaymentReturn = async () => {
      const uParams = new URLSearchParams(window.location.search);
      const hParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      
      const errorMsg = uParams.get('error_msg') || hParams.get('error_msg');
      const returnMerchantUid = uParams.get('merchant_uid') || hParams.get('merchant_uid');
      const isOrderSuccess = uParams.get('orderNumber') || hParams.get('orderNumber');
      const isReturningFromPG = uParams.get('imp_uid') || hParams.get('imp_uid') || returnMerchantUid || errorMsg;

      if (errorMsg) {
        if (returnMerchantUid) {
          deleteDoc(doc(db, "orders", returnMerchantUid))
            .then(() => console.log("Cancelled order deleted:", returnMerchantUid))
            .catch(err => console.error("Error deleting cancelled order:", err));
        }
        if (window.customAlert) {
          await window.customAlert(decodeURIComponent(errorMsg));
        } else {
          alert(decodeURIComponent(errorMsg));
        }
      }

      if (isReturningFromPG || isOrderSuccess) {
        if (isOrderSuccess || (isReturningFromPG && !errorMsg)) {
          const oid = isOrderSuccess || returnMerchantUid;
          if (oid) {
            updateDoc(doc(db, "orders", oid), { status: 'PAYMENT_COMPLETE' })
              .catch(e => console.log("Client-side update skipped. Relying on Webhook."));
          }
          
          setCartItems([]);
          localStorage.removeItem('makerstory_cart_items');

          if (window.customAlert) await window.customAlert("주문이 성공적으로 완료되었습니다.");
          else alert("주문이 성공적으로 완료되었습니다.");
        }

        const cleanUrl = `${window.location.origin}${window.location.pathname}#/shop`;
        if (window.location.href !== cleanUrl) {
           setTimeout(() => {
              window.history.replaceState(null, '', cleanUrl);
           }, 500);
        }
      } 
    };
    checkPaymentReturn();
  }, [navigate, location]);

  useEffect(() => {
    localStorage.setItem('makerstory_cart_items', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    let unsubUsers: (() => void) | null = null;
    let unsubOrders: (() => void) | null = null;
    let unsubCancelledOrders: (() => void) | null = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: any) => {
      if (firebaseUser) {
        const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data() as User;
          setCurrentUser(userData);
          localStorage.setItem('makerstory_user_cache', JSON.stringify(userData));
          
          if (userData.isAdmin === true) {
            setIsAdmin(true);
            localStorage.setItem('makerstory_admin', 'true');

            if (unsubUsers) unsubUsers();
            unsubUsers = onSnapshot(collection(db, "users"), (snapshot) => {
              const dbUsers: User[] = [];
              snapshot.forEach((doc) => dbUsers.push(doc.data() as User));
              setUsers(dbUsers);
            });

            if (unsubOrders) unsubOrders();
            unsubOrders = onSnapshot(query(collection(db, "orders"), orderBy("createdAt", "desc")), (snapshot) => {
              const dbOrders: Order[] = [];
              snapshot.forEach((doc) => dbOrders.push({ id: doc.id, ...doc.data() } as Order));
              setOrders(dbOrders);
            });

            if (unsubCancelledOrders) unsubCancelledOrders();
            unsubCancelledOrders = onSnapshot(query(collection(db, "cancelled_orders"), orderBy("createdAt", "desc")), (snapshot) => {
              const dbCancelled: Order[] = [];
              snapshot.forEach((doc) => dbCancelled.push({ id: doc.id, ...doc.data() } as Order));
              setCancelledOrders(dbCancelled);
            });
          } else {
            setIsAdmin(false);
            localStorage.removeItem('makerstory_admin');
            
            if (unsubUsers) { unsubUsers(); unsubUsers = null; }
            setUsers([]);

            if (unsubCancelledOrders) { unsubCancelledOrders(); unsubCancelledOrders = null; }
            setCancelledOrders([]);

            if (unsubOrders) unsubOrders();
            unsubOrders = onSnapshot(query(collection(db, "orders"), where("userId", "==", firebaseUser.uid)), (snapshot) => {
              const dbOrders: Order[] = [];
              snapshot.forEach((doc) => dbOrders.push({ id: doc.id, ...doc.data() } as Order));
              dbOrders.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
              setOrders(dbOrders);
            });
          }
        } else {
          // No user doc
          if (unsubUsers) { unsubUsers(); unsubUsers = null; }
          setUsers([]);
          if (unsubOrders) { unsubOrders(); unsubOrders = null; }
          setOrders([]);
          if (unsubCancelledOrders) { unsubCancelledOrders(); unsubCancelledOrders = null; }
          setCancelledOrders([]);
        }
      } else {
        setCurrentUser(null);
        localStorage.removeItem('makerstory_user_cache');
        setIsAdmin(false);
        localStorage.removeItem('makerstory_admin');

        if (unsubUsers) { unsubUsers(); unsubUsers = null; }
        setUsers([]);
        if (unsubOrders) { unsubOrders(); unsubOrders = null; }
        setOrders([]);
        if (unsubCancelledOrders) { unsubCancelledOrders(); unsubCancelledOrders = null; }
        setCancelledOrders([]);
      }
      setIsAuthLoading(false);
    });

    const unsubProducts = onSnapshot(collection(db, "products"), (snapshot) => {
      const dbProducts: Product[] = [];
      snapshot.forEach((doc) => dbProducts.push(doc.data() as Product));
      setProducts(dbProducts);
      localStorage.setItem('makerstory_products_cache', JSON.stringify(dbProducts));
    });

    getDoc(doc(db, "settings", "categories")).then(docSnap => {
      if (docSnap.exists()) setCategories(docSnap.data().list);
    });

    return () => { 
      unsubscribe(); 
      unsubProducts(); 
      if (unsubUsers) unsubUsers(); 
      if (unsubOrders) unsubOrders(); 
      if (unsubCancelledOrders) unsubCancelledOrders();
    };
  }, []);

  const onAddProduct = async (p: Product) => { await setDoc(doc(db, "products", p.id), p); };
  const onUpdateProduct = async (p: Product) => { await setDoc(doc(db, "products", p.id), p); };
  const onDeleteProduct = async (id: string) => { await deleteDoc(doc(db, "products", id)); };
  const onUpdateUserPoints = async (uid: string, points: number) => { await updateDoc(doc(db, "users", uid), { points }); };
  const onUpdateUser = async (u: User) => { await setDoc(doc(db, "users", u.id), u); };

  const onDeleteUser = async (uid: string) => { 
    await deleteDoc(doc(db, "users", uid)); 
    if (currentUser?.id === uid) {
      await handleUserLogout();
    }
  };

  const onUpdateOrderStatus = async (oid: string, status: OrderStatus, tracking?: any) => {
    const order = orders.find(o => o.id === oid);
    if (status === 'CANCELED' && order && order.status === 'PAYMENT_COMPLETE') {
      const confirmed = window.confirm("정말 주문을 취소 처리하시겠습니까?\n결제된 금액은 즉시 환불됩니다.");
      if (!confirmed) return;
      try {
        const result = await cancelPayment(order.orderNumber, "관리자 권한에 의한 취소");
        if (!result.success) {
          alert(`환불 실패: ${result.message}`);
          return;
        }
        if (order.pointsUsed && order.pointsUsed > 0) {
          const userRef = doc(db, "users", order.userId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data() as User;
            await updateDoc(userRef, { points: userData.points + order.pointsUsed });
          }
        }
      } catch (e: any) {
         alert(`주문 취소 처리 중 오류가 발생했습니다. (${e.message})`);
         return;
      }
    }

    const data: any = { status };
    if (tracking) { data.trackingNumber = tracking.number; data.trackingCompany = tracking.company; }
    await updateDoc(doc(db, "orders", oid), data);
  };
  const onUpdateCategories = async (cats: string[]) => {
    setCategories(cats);
    await setDoc(doc(db, "settings", "categories"), { list: cats });
  };

  const handleUserLogout = async () => { 
    await signOut(auth); 
    setCurrentUser(null); 
    setIsAdmin(false); 
    localStorage.removeItem('makerstory_user_cache'); 
    localStorage.removeItem('makerstory_admin'); 
    navigate('/shop', { replace: true }); 
  };

  const handleCheckout = async (method: string, pointsUsed: number = 0) => {
    if (!currentUser) { navigate('/login'); return; }
    
    const merchant_uid = `order_${Date.now()}`;
    const totalAmount = cartItems.reduce((acc, cur) => acc + (cur.price * cur.quantity), 0) + (cartItems[0].shippingFee || 0) - pointsUsed;

    const orderData: Order = {
      id: merchant_uid,
      orderNumber: merchant_uid,
      userId: currentUser.id,
      userName: currentUser.name,
      userPhone: currentUser.phone,
      userAddress: currentUser.address || '',
      userDetailAddress: currentUser.detailAddress || '',
      items: cartItems,
      shippingFee: cartItems.length > 0 ? cartItems[0].shippingFee : 0,
      totalAmount: totalAmount,
      pointsUsed: pointsUsed,
      paymentMethod: method,
      status: 'PENDING',
      createdAt: new Date().toISOString()
    };

    try {
      await setDoc(doc(db, "orders", merchant_uid), orderData);

      const paymentData = {
        merchant_uid: merchant_uid,
        name: cartItems.length > 1 ? `${cartItems[0].name} 외 ${cartItems.length - 1}건` : cartItems[0].name,
        amount: totalAmount,
        buyer_email: currentUser.email,
        buyer_name: currentUser.name,
        buyer_tel: currentUser.phone,
        buyer_addr: currentUser.address || '',
        buyer_postcode: '',
        userId: currentUser.id
      };

      requestPayment(paymentData, async (rsp) => { 
        try {
          await updateDoc(doc(db, "orders", rsp.merchant_uid), { status: 'PAYMENT_COMPLETE' });
        } catch (e) {}
        setCartItems([]); 
        navigate(`/shop?orderNumber=${rsp.merchant_uid}`, { replace: true }); 
      }, async (err) => { 
        await deleteDoc(doc(db, "orders", merchant_uid));
        if (window.customAlert) await window.customAlert(`결제 실패: ${err.error_msg}`);
        else alert(`결제 실패: ${err.error_msg}`); 
      });

    } catch (e) {
      if (window.customAlert) await window.customAlert("주문 처리 중 오류가 발생했습니다.");
      else alert("주문 처리 중 오류가 발생했습니다.");
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    const order = orders.find(o => o.id === orderId);
    if (!order) return;

    if (order.status !== 'PAYMENT_COMPLETE' && order.status !== 'PENDING') {
      if (window.customAlert) await window.customAlert("이미 상품 준비 중이거나 배송이 시작된 주문은 즉시 취소가 불가능합니다.\n고객센터로 문의해주세요.");
      else alert("이미 상품 준비 중이거나 배송이 시작된 주문은 즉시 취소가 불가능합니다.\n고객센터로 문의해주세요.");
      return;
    }

    const confirmed = window.customConfirm ? await window.customConfirm("정말 주문을 취소하시겠습니까?\n결제된 금액은 즉시 환불됩니다.") : window.confirm("정말 주문을 취소하시겠습니까?\n결제된 금액은 즉시 환불됩니다.");
    if (!confirmed) return;

    try {
      if (order.status === 'PAYMENT_COMPLETE') {
        const result = await cancelPayment(order.orderNumber, "사용자 요청에 의한 즉시 취소");
        if (!result.success) {
          if (window.customAlert) await window.customAlert(`환불 실패: ${result.message}`);
          else alert(`환불 실패: ${result.message}`);
          return;
        }
      }

      if (order.pointsUsed && order.pointsUsed > 0) {
        const userRef = doc(db, "users", order.userId);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data() as User;
          await updateDoc(userRef, { points: userData.points + order.pointsUsed });
        }
      }

      await updateDoc(doc(db, "orders", orderId), { status: 'CANCELED' });
      
      if (window.customAlert) await window.customAlert("주문이 정상적으로 취소되었으며, 결제 금액이 환불되었습니다.");
      else alert("주문이 정상적으로 취소되었으며, 결제 금액이 환불되었습니다.");

    } catch (e: any) {
      if (window.customAlert) await window.customAlert(`주문 취소 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요. (${e.message})`);
      else alert(`주문 취소 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요. (${e.message})`);
    }
  };

  const addToCart = useCallback((newItem: Omit<CartItem, 'cartId'>, startRect?: DOMRect) => {
    if (startRect && window.anime) {
      const cartBtn = document.getElementById('navbar-cart-btn');
      if (cartBtn) {
        const endRect = cartBtn.getBoundingClientRect();
        
        const flyImg = document.createElement('img');
        flyImg.src = newItem.image;
        flyImg.style.cssText = `position:fixed; z-index:2000; width:60px; height:60px; border-radius:50%; border:3px solid white; box-shadow:0 10px 20px rgba(79, 70, 229, 0.5); object-fit:cover; pointer-events:none;`;
        flyImg.style.left = `${startRect.left}px`;
        flyImg.style.top = `${startRect.top}px`;
        document.body.appendChild(flyImg);

        const timeline = window.anime.timeline({
          complete: () => {
            document.body.removeChild(flyImg);
            window.anime({
              targets: cartBtn,
              scale: [1, 1.4, 0.9, 1.1, 1],
              rotate: [0, -10, 10, -5, 5, 0],
              duration: 600,
              easing: 'easeOutElastic(1, .5)'
            });
          }
        });

        timeline.add({
          targets: flyImg,
          left: endRect.left,
          top: endRect.top,
          width: 20,
          height: 20,
          rotate: '3turn', 
          opacity: [1, 0.7],
          scale: [
            { value: 1.5, duration: 200, easing: 'easeOutSine' }, 
            { value: 0.5, duration: 600, easing: 'easeInQuad' }  
          ],
          duration: 800,
          easing: 'easeInOutBack' 
        });
      }
    }
    setCartItems(prev => {
      const idx = prev.findIndex(item => item.id === newItem.id);
      if (idx > -1) { const newCart = [...prev]; newCart[idx].quantity += newItem.quantity; return newCart; }
      return [...prev, { ...newItem, cartId: `cart-${Date.now()}` } as CartItem];
    });
  }, []);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      {/* 에러 원인 제거: <GlobalDialog /> 컴포넌트 삭제됨 */}
      <Route element={<ShoppingLayout isAuthLoading={isAuthLoading} isPaymentReturn={isPaymentReturn} cartItems={cartItems} setIsCartOpen={setIsCartOpen} isAdmin={isAdmin} currentUser={currentUser} handleUserLogout={handleUserLogout} setCartItems={setCartItems} isCartOpen={isCartOpen} handleCheckout={handleCheckout} products={products} />}>
        <Route path="/shop" element={<HomePage products={products} categories={categories} addToCart={addToCart} />} />
        <Route path="/homepage" element={<BrandStoryPage />} />
        <Route path="/competitions" element={<CompetitionsPage isAdmin={isAdmin} />} />
        <Route path="/library" element={<LibraryPage />} />
        <Route path="/product/:id" element={<ProductDetailPage products={products} onAddToCart={addToCart} currentUser={currentUser} isAdmin={isAdmin} />} />
        <Route path="/login" element={<UserLogin />} />
        <Route path="/mypage" element={<MyPage currentUser={currentUser} orders={orders} cancelledOrders={cancelledOrders} onUpdateUserInfo={onUpdateUser} onDeleteAccount={onDeleteUser} onCancelOrder={handleCancelOrder} />} />
        <Route path="/admin" element={isAdmin ? <Navigate to="/admin/dashboard" replace /> : <AdminLogin onLogin={() => { setIsAdmin(true); localStorage.setItem('makerstory_admin', 'true'); }} />} />
        <Route path="/admin/dashboard" element={isAdmin ? <AdminDashboard products={products} users={users} orders={orders} categories={categories} onUpdateCategories={onUpdateCategories} onAddProduct={onAddProduct} onUpdateProduct={onUpdateProduct} onDeleteProduct={onDeleteProduct} onUpdateUserPoints={onUpdateUserPoints} onUpdateUser={onUpdateUser} onDeleteUser={onDeleteUser} onUpdateOrderStatus={onUpdateOrderStatus} /> : <Navigate to="/admin" replace />} />
        <Route path="/success" element={<div className="py-20 text-center"><h2 className="text-3xl font-black mb-4 uppercase italic">Payment Success</h2><button onClick={() => navigate('/shop', { replace: true })} className="px-8 py-4 bg-indigo-600 text-white font-black rounded-2xl">Back to Shop</button></div>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (<HashRouter><MainApp /></HashRouter>);
export default App;